import Phaser from "phaser";
import { Button } from "@ravalmatic/catafractum-utils";
import { Difficulty } from "@ravalmatic/euchre-engine";
import { GameConstants } from "../../GameConstants";
import { GameManager } from "../../GameManager";
import { Preferences } from "../../Preferences";
import { button, label } from "../../ui/Layout";
import { AudioButtonsContainer } from "./AudioButtonsContainer";
import { LanguageSelectionLayer } from "./LanguageSelectionLayer";

export class SplashScene extends Phaser.Scene {
    private scaledItemsContainer!: Phaser.GameObjects.Container;
    private buttonsContainer!: Phaser.GameObjects.Container;
    private background!: Phaser.GameObjects.Image;
    private backgroundCover!: Phaser.GameObjects.Rectangle;
    private gameLogo!: Phaser.GameObjects.Image;
    private audioButtonsContainer!: AudioButtonsContainer;
    private difficultyButtons!: { difficulty: Difficulty; button: Button }[];
    private btnPlay!: Button;
    private btnTutorial!: Button;
    private btnLanguage!: Button;
    private languageButtonPressPointer?: Phaser.Input.Pointer;
    private subtitle!: Phaser.GameObjects.Text;
    private botNotice!: Phaser.GameObjects.Text;
    private alphaNotice!: Phaser.GameObjects.Text;
    private modal?: Phaser.GameObjects.Container;
    private modalBackdrop?: Phaser.GameObjects.Rectangle;

    constructor() { super("SplashScene"); }

    public create(): void {

        GameManager.setCurrentScene(this);
        this.modal = undefined;
        this.modalBackdrop = undefined;
        this.languageButtonPressPointer = undefined;
        const { width, height } = this.scale;
        this.background = new Phaser.GameObjects.Image(this, 0, 0, "splash_landscape");
        this.add.existing(this.background);
        this.backgroundCover = new Phaser.GameObjects.Rectangle(this, 0, 0, width, height, 0x000000, .4);
        this.add.existing(this.backgroundCover);
        this.audioButtonsContainer = new AudioButtonsContainer(this);
        this.add.existing(this.audioButtonsContainer);
        this.scaledItemsContainer = new Phaser.GameObjects.Container(this);
        this.add.existing(this.scaledItemsContainer);
        this.btnTutorial = new Button(this, 60, 60, "texture_atlas_1", "btn_tutorial");
        this.btnTutorial.setInflationTween(1.05);
        this.btnTutorial.onDown(() => this.showTutorial(), this);
        this.add.existing(this.btnTutorial);
        this.btnLanguage = new Button(this, 155, 60, "texture_atlas_1", "btn_language");
        this.btnLanguage.setInflationTween(1.05);
        this.btnLanguage.onDown((pointer: any) => {
            this.languageButtonPressPointer = pointer;
        }, this);
        this.btnLanguage.onUp((pointer: any) => {
            const wasPressed = this.languageButtonPressPointer === pointer;
            this.languageButtonPressPointer = undefined;
            if (wasPressed) this.showLanguageMenu();
        }, this);
        this.btnLanguage.on("pointerupoutside", (pointer: Phaser.Input.Pointer) => {
            if (this.languageButtonPressPointer === pointer) this.languageButtonPressPointer = undefined;
        });
        this.add.existing(this.btnLanguage);
        this.gameLogo = this.add.image(width / 2, 230, "texture_atlas_1", "game_logo");

        this.subtitle = this.add.text(width / 2, 404,
            Preferences.language === "es" ? "Parejas clasicas  |  Primero a 10" : "Classic partnerships  |  First to 10", {
                fontFamily: "NunitoSans", fontSize: "30px", color: GameConstants.TEXT_COLOR,
                align: "center", stroke: "#03142e", strokeThickness: 2
            }).setOrigin(.5).setShadow(0, 4, "rgba(0,0,0,0.8)", 4, false, true);
        const difficulties = [Difficulty.EASY, Difficulty.MEDIUM, Difficulty.HARD];
        this.buttonsContainer = new Phaser.GameObjects.Container(this);
        this.scaledItemsContainer.add(this.buttonsContainer);
        this.difficultyButtons = difficulties.map((difficulty, index) => {
            const selected = Preferences.difficulty === difficulty;
            const suffix = difficulty === Difficulty.HARD ? "expert" : difficulty;
            const image = new Button(
                this, width / 2 + (index - 1) * 215, height * .51,
                "texture_atlas_1", `btn_difficulty_${suffix}_${selected ? "on" : "off"}`
            ).setInteractive({ useHandCursor: true });
            image.setInflationTween(1.025);
            image.onDown(() => this.onClickDifficultyButton(difficulty), this);
            this.buttonsContainer.add(image);
            return { difficulty, button: image };
        });
        this.botNotice = label(this, this.buttonsContainer, width / 2, height * .60, Preferences.language === "es"
            ? "Por ahora, todos los niveles usan los mismos bots sencillos."
            : "For now, all levels use the same simple bots.", 24);
        this.btnPlay = new Button(this, width / 2, height * .73, "texture_atlas_1", "btn_play")
            .setInteractive({ useHandCursor: true });
        this.btnPlay.setInflationTween(1.05);
        this.btnPlay.onDown(this.onClickPlayButton, this);
        this.buttonsContainer.add(this.btnPlay);
        this.alphaNotice = label(this, this.scaledItemsContainer, width / 2, height - 35, `${GameConstants.VERSION}  |  ${Preferences.notice || "Stick the dealer"}`, 22);
        this.styleMenuText(this.scaledItemsContainer);
        this.relayout();
        this.scale.on("resize", this.relayout, this);
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.scale.off("resize", this.relayout, this));
    }

    public relayout(): void {
        
        if (!this.scaledItemsContainer) return;
        const { width, height } = this.scale;
        this.background.setTexture(width >= height ? "splash_landscape" : "splash_portrait");
        this.background.setPosition(width / 2, height / 2)
            .setScale(Math.max(width / this.background.width, height / this.background.height));
        this.backgroundCover.setPosition(width / 2, height / 2).setSize(width, height);
      
        this.scaledItemsContainer.setPosition(width / 2, height / 2 + 40)
        this.gameLogo.setX(width / 2);
        this.subtitle.setX(width / 2);
        this.difficultyButtons.forEach(({ button }, index) => button.setPosition((index - 1) * 215, 29));
        this.botNotice.setPosition(0, 90 + 20 );
        this.btnPlay.setPosition(0, 207);
        this.btnTutorial.setPosition(60, 60);
        this.btnLanguage.setPosition(155, 60);
        this.alphaNotice.setPosition(0, (height / 2 - 40) - 35);
        this.audioButtonsContainer.setPosition(width - 110, 60).setScale(1);
        this.modalBackdrop?.setSize(width, (height + 80) );
        if (this.modal instanceof LanguageSelectionLayer) this.modal.relayout();
    }

    private onClickDifficultyButton(difficulty: Difficulty): void {
        if (this.modal || Preferences.difficulty === difficulty) return;
        Preferences.setDifficulty(difficulty);
        this.refreshDifficultyButtons();
    }

    private refreshDifficultyButtons(): void {
        this.difficultyButtons.forEach(({ difficulty, button }) => {
            const suffix = difficulty === Difficulty.HARD ? "expert" : difficulty;
            button.setFrame(`btn_difficulty_${suffix}_${Preferences.difficulty === difficulty ? "on" : "off"}`);
        });
    }

    private onClickPlayButton(): void {
        if (!this.modal) GameManager.enterBoardScene();
    }

    private refreshLanguage(): void {
        this.subtitle.setText(Preferences.language === "es" ? "Parejas clasicas  |  Primero a 10" : "Classic partnerships  |  First to 10");
        this.botNotice.setText(Preferences.language === "es"
            ? "Por ahora, todos los niveles usan los mismos bots sencillos."
            : "For now, all levels use the same simple bots.");
        this.closeModal();
    }

    private closeModal(): void {
        this.modal?.destroy();
        this.modal = undefined;
        this.modalBackdrop = undefined;
    }

    private styleMenuText(container: Phaser.GameObjects.Container): void {
        container.each((item: Phaser.GameObjects.GameObject) => {
            if (item instanceof Phaser.GameObjects.Text) {
                item.setShadow(0, 4, "rgba(0,0,0,0.8)", 4, false, true);
            } else if (item instanceof Phaser.GameObjects.Container) {
                this.styleMenuText(item);
            }
        });
    }

    private showLanguageMenu(): void {
        if (this.modal) return;
        this.modal = new LanguageSelectionLayer(this, () => this.refreshLanguage());
        this.add.existing(this.modal);
    }

    private showTutorial(): void {
        if (this.modal) return;
        const width = 900;
        const height = 900;
        const layer = this.modal = new Phaser.GameObjects.Container(this, -450, -450);
        this.scaledItemsContainer.add(layer);
        this.modalBackdrop = new Phaser.GameObjects.Rectangle(this, width / 2, height / 2, width, height, 0x2A0B20, .98)
            .setInteractive();
        layer.add(this.modalBackdrop);
        this.relayout();
        label(this, layer, width / 2, height / 2 - 230, "TUTORIAL", 44)
            .setFontFamily("Baloo2-SemiBold");
        label(this, layer, width / 2, height / 2, (Preferences.language === "es" ? [
            "Tu y Norte jugais contra Oeste y Este.",
            "24 cartas: 9, 10, J, Q, K, A. Cinco bazas por mano.",
            "Elige triunfo o pasa. Ronda 1: el repartidor",
            "recoge la carta descubierta y descarta otra.",
            "Ronda 2: otro palo. El repartidor no puede pasar.",
            "Asiste al palo. La J del mismo color es triunfo.",
            "J triunfo > J mismo color > A > K > Q > 10 > 9.",
            "Ganar 3 o 4 bazas: 1 punto. Las 5: 2 puntos.",
            "Solo y las 5: 4 puntos. Euchre: 2 al rival.",
            "La primera pareja en llegar a 10 gana."
        ] : [
            "You and North play against West and East.",
            "24 cards: 9, 10, J, Q, K, A. Five tricks per hand.",
            "Choose trump or pass. Round 1: dealer picks up",
            "the upcard and discards. Round 2: choose another suit.",
            "The dealer must choose if everyone else passes.",
            "Follow suit. The same-color Jack belongs to trump.",
            "Trump Jack > same-color Jack > A > K > Q > 10 > 9.",
            "Win 3 or 4 tricks: 1 point. All 5: 2 points.",
            "Alone and all 5: 4 points. Euchred: opponents get 2.",
            "First partnership to 10 points wins."
        ]).join("\n"), 27).setLineSpacing(10);
        button(this, layer, width / 2, height / 2 + 265,
            Preferences.language === "es" ? "Volver" : "Back", () => this.closeModal());
        this.styleMenuText(layer);
    }
}
